// LazyAssetLoader.js - 按需加载缺失纹理/精灵图
import DebugHelper from './DebugHelper.js';
import { RESOURCE_CONFIG } from './ResourceConfig.js';
import { resolveBattleImageResources, resolveMapTileResources } from './AssetCatalog.js';
import { collectMapTextureKeys } from './MapAssetScanner.js';
import { getVehicleTextureKey, isTankSpritesheetKey } from './VehicleTextureHelper.js';

/** @type {WeakMap<Phaser.Scene, Promise<void>>} */
const sceneLoadChains = new WeakMap();

/**
 * 同一场景内串行化 Loader 请求，避免并发 attach complete 监听导致提前 resolve
 * @param {Phaser.Scene} scene
 * @param {() => Promise<void>} task
 * @returns {Promise<void>}
 */
function enqueueSceneLoad(scene, task) {
    const previous = sceneLoadChains.get(scene) || Promise.resolve();
    const next = previous
        .then(task)
        .catch((error) => {
            throw error;
        });
    sceneLoadChains.set(scene, next.catch(() => {}));
    return next;
}

/**
 * @param {Phaser.Scene} scene
 * @param {Array<{ key: string, path: string, type?: string, frameWidth?: number, frameHeight?: number }>} assets
 * @returns {Promise<void>}
 */
export function loadMissingAssets(scene, assets) {
    const pending = assets.filter((asset) => asset.key && !scene.textures.exists(asset.key));

    if (pending.length === 0) {
        return Promise.resolve();
    }

    return enqueueSceneLoad(scene, () => new Promise((resolve, reject) => {
        pending.forEach((asset) => {
            if (asset.type === 'spritesheet') {
                scene.load.spritesheet(asset.key, asset.path, {
                    frameWidth: asset.frameWidth || 32,
                    frameHeight: asset.frameHeight || 32
                });
            } else {
                scene.load.image(asset.key, asset.path);
            }
        });

        const onComplete = () => {
            scene.load.off('loaderror', onError);
            resolve();
        };
        const onError = (file) => {
            scene.load.off('complete', onComplete);
            reject(new Error(`资源加载失败: ${file?.key || 'unknown'}`));
        };

        scene.load.once('complete', onComplete);
        scene.load.once('loaderror', onError);

        if (!scene.load.isLoading()) {
            scene.load.start();
        }
    }));
}

/**
 * 确保地图所需图块已加载
 * @param {Phaser.Scene} scene
 * @param {Object} mapJson
 * @returns {Promise<void>}
 */
export async function ensureMapTextures(scene, mapJson) {
    const textureKeys = collectMapTextureKeys(mapJson);
    const resources = resolveMapTileResources(textureKeys).map(({ key, path }) => ({
        key,
        path,
        type: 'image'
    }));

    if (resources.length === 0) {
        return;
    }

    DebugHelper.log(`LazyAssetLoader: 按需加载地图图块 ${resources.length} 个`);
    await loadMissingAssets(scene, resources);
}

/**
 * 收集战斗所需图片 key
 * @param {Object} battleData
 * @returns {Set<string>}
 */
export function collectBattleTextureKeys(battleData) {
    const keys = new Set();

    const addPlayerTextures = (playerData) => {
        if (!playerData) return;
        if (playerData.battle) keys.add(playerData.battle);
        if (playerData.inVehicle && playerData.vehicle) {
            const tankKey = getVehicleTextureKey(playerData.vehicle);
            if (tankKey && !isTankSpritesheetKey(tankKey)) {
                keys.add(tankKey);
            }
        }
    };

    addPlayerTextures(battleData?.player);
    (battleData?.enemies || []).forEach((enemy) => {
        if (enemy?.name) keys.add(enemy.name);
    });
    (battleData?.teamMembers || []).forEach((member) => {
        addPlayerTextures(member.playerData);
    });

    return keys;
}

/**
 * 确保战斗所需立绘与特效已加载
 * @param {Phaser.Scene} scene
 * @param {Object} battleData
 * @returns {Promise<void>}
 */
export async function ensureBattleAssets(scene, battleData) {
    const battleImages = resolveBattleImageResources(collectBattleTextureKeys(battleData)).map(({ key, path }) => ({
        key,
        path,
        type: 'image'
    }));

    const effectSpritesheets = (RESOURCE_CONFIG.battleEffects || []).map((effect) => ({
        key: effect.key,
        path: effect.path,
        type: 'spritesheet',
        frameWidth: effect.width,
        frameHeight: effect.height
    }));

    const assets = [...battleImages, ...effectSpritesheets];
    const missingCount = assets.filter((asset) => !scene.textures.exists(asset.key)).length;

    if (missingCount > 0) {
        DebugHelper.log(`LazyAssetLoader: 按需加载战斗资源 ${missingCount} 个`);
        await loadMissingAssets(scene, assets);
    }

    const audioKeys = collectBattleAudioKeys(battleData);
    await ensureAudios(scene, audioKeys);
}

/**
 * 收集战斗所需音频 key
 * @param {Object} battleData
 * @returns {string[]}
 */
function collectBattleAudioKeys(battleData) {
    const keys = new Set(['battle_bgm', 'victory_bgm', 'level_up_bgm', 'boss_bgm']);
    if (battleData?.bgm) {
        keys.add(battleData.bgm);
    }
    (RESOURCE_CONFIG.soundEffects || []).forEach((entry) => {
        if (entry?.key) {
            keys.add(entry.key);
        }
    });
    return [...keys];
}

/**
 * 从资源配置中解析音频条目
 * @param {string[]} audioKeys
 * @returns {Array<{ key: string, path: string }>}
 */
function resolveAudioResources(audioKeys) {
    const catalog = [
        ...(RESOURCE_CONFIG.essentialAudio || []),
        ...(RESOURCE_CONFIG.bgm || []),
        ...(RESOURCE_CONFIG.soundEffects || [])
    ];
    const keySet = new Set(audioKeys);
    return catalog.filter((entry) => keySet.has(entry.key));
}

/**
 * 确保音频已加载（BGM/音效按需加载）
 * @param {Phaser.Scene} scene
 * @param {string[]} audioKeys
 * @returns {Promise<void>}
 */
export function ensureAudios(scene, audioKeys) {
    const resources = resolveAudioResources(audioKeys);
    const pending = resources.filter((entry) => entry.key && !scene.cache.audio.exists(entry.key));

    if (pending.length === 0) {
        return Promise.resolve();
    }

    return enqueueSceneLoad(scene, () => new Promise((resolve, reject) => {
        pending.forEach((entry) => {
            scene.load.audio(entry.key, entry.path);
        });

        const onComplete = () => {
            scene.load.off('loaderror', onError);
            resolve();
        };
        const onError = (file) => {
            scene.load.off('complete', onComplete);
            reject(new Error(`音频加载失败: ${file?.key || 'unknown'}`));
        };

        scene.load.once('complete', onComplete);
        scene.load.once('loaderror', onError);

        if (!scene.load.isLoading()) {
            scene.load.start();
        }
    }));
}

/**
 * 播放音效（未缓存时按需加载，加载失败则静默跳过）
 * @param {Phaser.Scene} scene
 * @param {string} key
 * @param {Object} [config]
 * @returns {Promise<void>}
 */
export function playSceneSfx(scene, key, config = {}) {
    if (!scene?.sound || !key) {
        return Promise.resolve();
    }

    const play = () => {
        if (scene.cache.audio.exists(key)) {
            scene.sound.play(key, config);
        }
    };

    if (scene.cache.audio.exists(key)) {
        play();
        return Promise.resolve();
    }

    return ensureAudios(scene, [key])
        .then(play)
        .catch((error) => {
            DebugHelper.warn(`LazyAssetLoader: 音效播放跳过 ${key}`, error?.message || error);
        });
}

/**
 * 切换场景 BGM（未缓存时按需加载，加载失败则静默跳过）
 * @param {Phaser.Scene} scene
 * @param {string} key
 * @param {Object} [config]
 * @returns {Promise<void>}
 */
export function playSceneBgm(scene, key, config = {}) {
    if (!scene?.sound || !key) {
        return Promise.resolve();
    }

    const bgmConfig = {
        loop: true,
        volume: config.volume ?? 0.1,
        ...config
    };

    const start = () => {
        if (!scene.cache.audio.exists(key)) {
            return;
        }
        if (scene.bgm) {
            scene.bgm.stop();
        }
        scene.bgm = scene.sound.add(key, bgmConfig);
        scene.bgm.play();
    };

    if (scene.cache.audio.exists(key)) {
        start();
        return Promise.resolve();
    }

    return ensureAudios(scene, [key])
        .then(start)
        .catch((error) => {
            DebugHelper.warn(`LazyAssetLoader: BGM 播放跳过 ${key}`, error?.message || error);
        });
}
